<script setup lang="ts">
import { vDialogContainer } from '@fusapp-work/ui-kit/cosmos'
import { computed, onMounted, ref, watch } from 'vue'
import { useApiClient, useI18n, useRouter, useRoute, SurfaceLanguageSelector } from '@fusapp-work/shell'
import { useCurrentSession } from '../../composables/useCurrentSession'
import { useSessionLogout } from '../../composables/useSessionLogout'
import ConsoleAccountLinks from './ConsoleAccountLinks.vue'
import ShellIcon from './ShellIcon.vue'
import ConsoleAccountIcon from './ConsoleAccountIcon.vue'
import { appReturnHref } from './appReturn'

const props = defineProps<{ sha: string | null; versionLoading: boolean; organizationName: string | undefined }>()
const panelOpen = ref(false)
const version = computed(() => props.sha ?? t(props.versionLoading ? 'home.versionLoading' : 'home.versionUnavailable'))
const { t } = useI18n()
const emit = defineEmits<{ search: [returnFocus: HTMLButtonElement | undefined] }>()
const { state } = useCurrentSession()
const router = useRouter(); const route = useRoute()
const auditTarget = computed(() => ({ name: 'organization-audit', query: typeof route.query.orgId === 'string' ? { orgId: route.query.orgId } : {} }))
const { isPending, hasError, signOut } = useSessionLogout(useApiClient(), () => router.replace({ name: 'login' }))
const identity = computed(() => state.session?.name?.trim() || state.session?.email?.trim() || state.session?.userId || '')
const initials = computed(() => identity.value.split(/\s+/).map(word => word[0]).join('').slice(0, 2).toLocaleUpperCase())
const roles = computed(() => state.session?.roles.join(', ') ?? '')
// Same App target as the sidebar link: a link when configured, inert with its reason otherwise (never contradictory).
const appHref = appReturnHref()
const accountTrigger = ref<HTMLButtonElement>()
const panel = ref<HTMLDialogElement>()
const confirmation = ref<HTMLDialogElement>()
function openPanel() { panelOpen.value = true; panel.value?.showModal() }
function closePanel() { panelOpen.value = false; panel.value?.close() }
watch(() => state.session?.userId, closePanel)
function requestLogout() { closePanel(); confirmation.value?.showModal() }
const selectedTheme = ref<'dark' | 'light'>('dark')
onMounted(() => { selectedTheme.value = document.documentElement.dataset['theme'] === 'light' ? 'light' : 'dark' })
function theme(value: 'dark' | 'light') { selectedTheme.value = value; document.documentElement.dataset['theme'] = value }
</script>

<template>
  <button
    ref="accountTrigger"
    class="console-account-trigger"
    data-testid="shell-account"
    :aria-label="t('consoleShell.account')"
    aria-haspopup="dialog"
    @click="openPanel"
  >
    <span class="console-avatar">{{ initials }}</span><span class="console-account-label"><span data-testid="home-user-identity">{{ identity }}</span><small data-testid="home-user-roles">{{ roles }}</small></span>
    <ShellIcon name="chevron" />
  </button><dialog
    ref="panel"
    v-dialog-container="{ close: closePanel, body: '.console-account-items' }"
    class="console-dialog console-account"
    :aria-label="t('consoleShell.account')"
    @click="($event.target === panel) && closePanel()"
    @close="panelOpen = false"
  >
    <div class="console-dialog-content">
      <header class="console-dialog-header">
        <span class="console-avatar">{{ initials }}</span><div><strong>{{ identity }}</strong><small>{{ state.session?.email }}</small></div><span
          v-if="state.session?.roles[0]"
          class="console-role"
        >{{ state.session.roles[0] }}</span>
      </header>
      <div class="console-account-scope">
        <div class="console-account-scope-row">
          <span class="console-status-dot" /><span>{{ organizationName || t('consoleShell.scope') }}</span><span class="console-role">{{ t('consoleShell.corporate') }}</span>
        </div>
        <small class="console-account-provider"><span>{{ t('consoleShell.identityProviderLabel') }}</span><span>{{ t('consoleShell.identityProvider') }}</span></small>
      </div><div class="console-account-items">
        <ConsoleAccountLinks
          v-if="panelOpen"
          @navigate="closePanel"
        />
        <div class="console-account-divider" />
        <a
          v-if="appHref"
          class="console-profile-link console-app-switch"
          data-testid="account-app-return"
          :href="appHref"
        >
          <ConsoleAccountIcon name="grid" /><span><strong>{{ t('consoleShell.accountAppReturn') }}</strong></span>
        </a><button
          v-else
          class="console-profile-link console-app-switch"
          data-testid="account-app-return"
          disabled
          aria-disabled="true"
          aria-describedby="console-account-app-return-reason"
          :title="t('consoleShell.appReturnUnavailable')"
        >
          <ConsoleAccountIcon name="grid" /><span><strong>{{ t('consoleShell.accountAppReturn') }}</strong><small id="console-account-app-return-reason">{{ t('consoleShell.appReturnUnavailable') }}</small></span>
        </button><router-link
          class="console-profile-link"
          :to="auditTarget"
          @click="closePanel"
        >
          <ConsoleAccountIcon name="log" /><span><strong>{{ t('consoleShell.audit') }}</strong><small>{{ t('consoleShell.accountAuditDescription') }}</small></span>
        </router-link>
        <button
          class="console-account-action"
          @click="closePanel(); emit('search', accountTrigger)"
        >
          <ConsoleAccountIcon name="key" /><span>{{ t('consoleShell.shortcuts') }}</span><kbd>⌘K</kbd>
        </button>
        <div class="console-account-divider" />
        <!-- Q-58: HelpDesk is Work-only; this Console entry point is Cosmos-only. -->
        <button
          class="console-signout"
          data-testid="logout-request"
          @click="requestLogout"
        >
          <ConsoleAccountIcon name="lock" />{{ t('login.signOut') }}
        </button>
      </div><div class="console-theme">
        <span>{{ t('consoleShell.theme') }}</span>
        <abbr
          data-testid="home-version"
          :title="version"
          :aria-label="`${t('consoleShell.version')}: ${version}`"
          tabindex="0"
        >ⓘ</abbr>
        <button
          :aria-pressed="selectedTheme === 'dark'"
          @click="theme('dark')"
        >
          {{ t('consoleShell.dark') }}
        </button><button
          :aria-pressed="selectedTheme === 'light'"
          @click="theme('light')"
        >
          {{ t('consoleShell.light') }}
        </button>
      </div><div class="console-language">
        <span id="console-language-label">{{ t('consoleShell.languagePanel') }}</span>
        <SurfaceLanguageSelector
          surface="console"
          data-testid="shell-language"
          labelled-by="console-language-label"
          :label="t('consoleShell.languagePanel')"
          :labels="{ tr: t('consoleShell.languageTr'), en: t('consoleShell.languageEn') }"
          :accessible-labels="{ tr: t('consoleShell.languageTrName'), en: t('consoleShell.languageEnName') }"
        />
      </div>
    </div>
  </dialog>
  <dialog
    ref="confirmation"
    v-dialog-container="{ busy: isPending, body: '.console-dialog-content', initial: 'button[autofocus]', returnFocus: () => accountTrigger }"
    role="alertdialog"
    class="console-dialog console-confirmation"
    :aria-label="t('consoleShell.signOutTitle')"
    @cancel="isPending && $event.preventDefault()"
  >
    <h2>{{ t('consoleShell.signOutTitle') }}</h2>
    <div class="console-dialog-content">
      <p>{{ t('consoleShell.signOutBody') }}</p>
      <p
        v-if="hasError"
        role="alert"
        data-testid="logout-error"
      >
        {{ t('login.errors.logout') }}
      </p>
    </div>
    <div class="console-dialog-actions">
      <button
        autofocus
        :disabled="isPending"
        @click="confirmation?.close()"
      >
        {{ t('consoleShell.cancel') }}
      </button><button
        class="console-danger"
        data-testid="logout-button"
        :disabled="isPending"
        :aria-busy="isPending"
        @click="signOut"
      >
        {{ t(isPending ? 'login.signingOut' : 'login.signOut') }}
      </button>
    </div>
  </dialog>
</template>

<style scoped>
.console-account { font-size:14px; }
.console-account-items :deep(.console-profile-link) { font-size:13px; }
.console-app-switch { color:var(--fw-text-primary); }
.console-account-items :deep(.console-profile-link>span:last-child) { flex:1;min-width:0;margin-right:10px; }
.console-account-scope { flex-direction:column;align-items:stretch;gap:6px; }
.console-account-scope-row { display:flex;gap:9px;align-items:center; }
.console-account-scope-row>span:nth-child(2) { flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
.console-account-scope small { font-size:11.5px; }.console-account-provider { display:flex;flex-direction:column;line-height:14px; }
.console-account-items { padding:6px; }
.console-account-items :deep(.console-account-links) { padding:0; }
.console-account-items :deep(.console-profile-link),.console-account-items .console-account-action,.console-account-items .console-signout { width:100%;margin:0;padding:8px 10px; }
.console-account-items :deep(.console-profile-link small) { margin-top:0; }
.console-account-items :deep(.console-profile-link strong) { display:block; }
.console-theme abbr { font-size:12px;cursor:help;text-decoration:none; }
</style>
